import type { UserData } from '@/stores/types'
import { useUserStore } from '@/stores/userStore'

let csrfToken: string | null = null
let sessionLoaded = false

// Lock compartilhado entre loadAuthSession e fetchWithRefresh.
// Garante que apenas uma chamada ao /oauth2/refresh ocorra por vez,
// evitando replay attack por rotação simultânea do refresh token.
let refreshing: Promise<{ ok: boolean; serverError: boolean }> | null = null

/**
 * Executa o refresh token com lock global.
 * Chamadas simultâneas aguardam a mesma promise em vez de disparar requests paralelas.
 *
 * Retorna:
 *  - { ok: true }                      → refresh bem-sucedido
 *  - { ok: false, serverError: false } → sessão inválida (401/403)
 *  - { ok: false, serverError: true }  → erro de rede ou 5xx
 */
export async function performRefresh(): Promise<{ ok: boolean; serverError: boolean }> {
  if (refreshing)
    return refreshing

  refreshing = (async () => {
    try {
      const { getDeviceFingerprint } = await import('@/utils/helpers/deviceFingerprint')

      const [currentCsrf, fingerprint] = await Promise.all([
        Promise.resolve(getCsrfToken()),
        getDeviceFingerprint(),
      ])

      const headers: Record<string, string> = {
        'X-Device-Fingerprint': fingerprint,
      }

      if (currentCsrf)
        headers['X-CSRF-Token'] = currentCsrf

      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/oauth2/refresh`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
        },
      )

      // Captura novo CSRF token se o backend o reemitiu
      const newCsrf = res.headers.get('X-CSRF-Token')
      if (newCsrf)
        csrfToken = newCsrf

      const status = res.status

      return {
        ok: res.ok,
        serverError: status === 0 || status >= 500,
      }
    }
    catch {
      // Erro de rede (ECONNREFUSED, timeout)
      return { ok: false, serverError: true }
    }
    finally {
      refreshing = null
    }
  })()

  return refreshing
}

/**
 * Busca os dados do usuário e o CSRF token via API `/oauth2/me`.
 * Armazena em memória (store + variável) em vez de depender de cookies legíveis.
 *
 * Deve ser chamado uma vez após o login (redirect do IDP) e após refresh de página.
 *
 * Retorna:
 *  - true  → sessão válida e carregada
 *  - false → sessão inválida (401/403 confirmado pelo backend) → deve deslogar
 *  - null  → erro de rede ou servidor indisponível → não deslogar, tentar novamente depois
 */
export async function loadAuthSession(): Promise<boolean | null> {
  if (sessionLoaded)
    return true

  try {
    let response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/oauth2/me`,
      {
        credentials: 'include',
      },
    )

    // Se 401, tenta refresh via lock compartilhado e repete
    if (response.status === 401) {
      const { ok, serverError } = await performRefresh()

      if (!ok) {
        sessionLoaded = false
        if (serverError)
          return null

        // 401/403 confirmado → sessão realmente inválida → deslogar
        const domainBase = import.meta.env.VITE_DOMAIN_BASE

        document.cookie = `_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domainBase}`

        return false
      }

      response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/oauth2/me`,
        {
          credentials: 'include',
        },
      )
    }

    // 5xx no /me → backend instável, não deslogar
    if (response.status >= 500) {
      sessionLoaded = false

      return null
    }

    if (!response.ok) {
      sessionLoaded = false

      return false
    }

    const data: UserData = await response.json()
    const userStore = useUserStore()

    userStore.userData = data

    const tempDr = sessionStorage.getItem('sw-active-dr')
    if (tempDr)
      userStore.setRegional(tempDr)

    // Inicializa o DR de sessão (usado pelo navbar para exibição)
    userStore.initSessionDr()

    // Captura o CSRF token do header da resposta
    const csrf = response.headers.get('X-CSRF-Token')
    if (csrf)
      csrfToken = csrf

    sessionLoaded = true

    return true
  }
  catch {
    // Erro de rede (ECONNREFUSED, timeout, backend reiniciando) → não deslogar
    sessionLoaded = false

    return null
  }
}

/**
 * Retorna o CSRF token armazenado em memória.
 * Fallback: lê do cookie (httpOnly: false).
 */
export function getCsrfToken(): string | null {
  if (csrfToken)
    return csrfToken

  // Tenta via useCookie (Vue composable)
  try {
    const cookieValue = useCookie('_host_csrf').value
    if (cookieValue)
      return String(cookieValue)
  }
  catch {
    // useCookie pode falhar fora do contexto Vue
  }

  // Fallback: lê direto do document.cookie
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|; )_host_csrf=([^;]*)/)
    if (match)
      return decodeURIComponent(match[1])
  }

  return null
}

/**
 * Atualiza o CSRF token em memória (ex: após refresh token).
 */
export function setCsrfTokenInMemory(token: string) {
  csrfToken = token
}

/**
 * Reseta o estado da sessão (usar no logout).
 */
export function resetAuthSession() {
  csrfToken = null
  sessionLoaded = false
}

/**
 * Executa logout do usuário: limpa cookie de sessão e redireciona para login.
 */
export function logout() {
  const domainBase = import.meta.env.VITE_DOMAIN_BASE

  document.cookie = `_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domainBase}`
  resetAuthSession()
  window.location.href = '/login'
}
